import * as React from 'react';
import {useEffect, useState} from 'react';
import {KatSpinner} from '@amzn/katal-react';
import {LighthouseApi} from 'src/app/api/LighthouseApi';
import {isSuccessResult} from 'src/app/constant/Constants';
import {LighthouseReviewData} from 'src/app/api/model/response/LighthouseReviewResponse';
import {SubmitDecisionRequest} from 'src/app/api/model/request/SubmitDecisionRequest';
import {ReviewResult, ReviewStatus} from 'src/app/constant/Enums';
import {AdditionalTab, HistoryEntry, AttachmentEntry} from 'src/app/model/lighthouse/Common';
import {CASE_DECISION_REASON_CODE_CONFIG, DEFAULT_REASON_CODE_OPTION} from 'src/app/config/DropDownConfig';
import CaseDetailsSection from 'src/app/component/lighthouse/review/CaseDetailsSection';
import AuditChecksSection from 'src/app/component/lighthouse/review/AuditChecksSection';
import DataPointsSection from 'src/app/component/lighthouse/review/DataPointsSection';
import CaseHistorySection from 'src/app/component/lighthouse/review/CaseHistorySection';
import DecisionFooter from 'src/app/component/lighthouse/review/DecisionFooter';
import TabContainer from 'src/app/component/lighthouse/review/TabContainer';
import UploadFileModal from 'src/app/component/common/UploadFileModal';
import UserInfoStore from 'src/app/store/UserInfoStore';
import {logger} from 'src/app/util/Logger';

/**
 * Props for the LighthouseReview page component.
 */
export interface LighthouseReviewPageProps {
    /** Review ID to load. If not provided, reads from URL query param `?reviewId=`. */
    reviewId?: string;
    /** Rendering mode: iframe (inside MCExecute), popup (CaseViewerModal), or standalone (direct URL). */
    mode?: 'iframe' | 'popup' | 'standalone';
    /** When true, hides DecisionFooter and disables editable fields. */
    readOnly?: boolean;
    /** Called when the popup modal should close (popup mode only). */
    onClose?: () => void;
}

/**
 * Registry for additional tab components.
 * New enforcement types register their custom tab components here.
 * The backend returns a `componentType` string that maps to a component in this registry.
 */
const TAB_REGISTRY: Record<string, React.FC<{data: any}>> = {
    // Future: IMAGE_COMPARISON: ImageComparisonTab,
};

/**
 * Lighthouse Review Page
 *
 * Renders enforcement cases for LMAQ auditors with a config-driven tab layout.
 * Supports three modes:
 *   - iframe: Loaded inside MCExecute, communicates via postMessage
 *   - popup: Opened as a modal from Management Page (read-only)
 *   - standalone: Direct URL navigation for testing/dev
 */
const LighthouseReview: React.FC<LighthouseReviewPageProps> = (props) => {
    const detectMode = (): 'iframe' | 'popup' | 'standalone' => {
        if (props.mode) return props.mode;
        if (window !== window.parent) return 'iframe';
        return 'standalone';
    };

    const mode = detectMode();

    const getInitialReviewId = (): string | null => {
        if (props.reviewId) return props.reviewId;
        const params = new URLSearchParams(window.location.search);
        return params.get('reviewId');
    };

    const [reviewId] = useState<string | null>(getInitialReviewId);
    const [reviewData, setReviewData] = useState<LighthouseReviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [auditCheckResponses, setAuditCheckResponses] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);

    // Attachment upload modal state
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    // Add-remark (comment) state
    const [addingComment, setAddingComment] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);

    const isReadOnly = props.readOnly || reviewData?.reviewStatus === ReviewStatus.COMPLETED;

    // Fetch review data when reviewId is available
    useEffect(() => {
        if (reviewId) {
            fetchReviewData();
        } else {
            setLoading(false);
        }
    }, [reviewId]);

    const fetchReviewData = async () => {
        if (!reviewId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const response = await LighthouseApi.getReview(reviewId);
            if (isSuccessResult(response.result) && response.data) {
                setReviewData(response.data);
            } else {
                setLoadError(response.msg || 'Failed to load review data');
            }
        } catch (err: any) {
            const errorMsg = err.message || 'Unknown error';
            setLoadError('Error loading review: ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleAuditCheckChange = (label: string, value: string) => {
        setAuditCheckResponses(prev => ({...prev, [label]: value}));
    };

    const openUploadModal = () => {
        setUploadError(null);
        setUploadModalVisible(true);
    };

    const closeUploadModal = () => {
        if (!uploading) {
            setUploadModalVisible(false);
            setUploadError(null);
        }
    };

    const handleViewAttachment = async (fileId: string) => {
        if (!reviewId) return;
        setDownloadError(null);
        try {
            await LighthouseApi.triggerAttachmentDownload(reviewId, fileId);
        } catch (err: any) {
            logger.error('Failed to download attachment', {reviewId, fileId, error: err?.message});
            setDownloadError('Failed to download the file. Please try again.');
        }
    };

    /**
     * Uploads a batch of files in parallel and optimistically appends the successful
     * ones to local history state (no re-fetch — this page runs in an iframe and a
     * reload would discard in-progress form input). Resolves with the files that
     * FAILED so the modal can keep only those staged for retry.
     *
     * - All succeed → append all, close the modal.
     * - Partial     → append the succeeded, surface a message, keep the failed staged.
     * - All fail    → append none, surface a message, keep all staged.
     */
    const handleUploadConfirm = async (files: File[]): Promise<File[]> => {
        if (!reviewId) return files;
        setUploading(true);
        setUploadError(null);
        try {
            // Upload all in parallel; each wrapper catches its own error so the batch
            // never short-circuits (equivalent to allSettled, without needing that lib target).
            const settled = await Promise.all(files.map(async (file) => {
                try {
                    const entry = await LighthouseApi.uploadAttachment(reviewId, file);
                    return {ok: true as const, entry};
                } catch {
                    return {ok: false as const, file};
                }
            }));

            const uploaded: AttachmentEntry[] = [];
            const failedFiles: File[] = [];
            settled.forEach(result => {
                if (result.ok) {
                    uploaded.push(result.entry);
                } else {
                    failedFiles.push(result.file);
                }
            });

            if (uploaded.length > 0) {
                setReviewData(prev => prev && {
                    ...prev,
                    history: {
                        ...prev.history,
                        attachments: [...prev.history.attachments, ...uploaded],
                    },
                });
            }

            if (failedFiles.length === 0) {
                setUploadModalVisible(false);
            } else {
                setUploadError(
                    `${uploaded.length} of ${files.length} uploaded. `
                    + `${failedFiles.length} failed — please retry.`
                );
            }
            return failedFiles;
        } finally {
            setUploading(false);
        }
    };

    /**
     * Posts the current Auditor Remarks text as a standalone comment and, on success,
     * optimistically appends it to the audit trail as a COMMENT entry. We do NOT
     * re-fetch the review — this page runs inside an iframe and a reload would discard
     * in-progress form input. Action is "COMMENT" to match the backend deriveAction
     * change (until it ships, a reload would show "UNKNOWN"); notes carries the text.
     */
    const handleAddRemark = async (remark: string): Promise<boolean> => {
        if (!reviewId) return false;
        const trimmed = remark.trim();
        if (trimmed.length === 0) {
            setCommentError('Please enter a remark before adding.');
            return false;
        }
        setAddingComment(true);
        setCommentError(null);
        try {
            const response = await LighthouseApi.addComment(reviewId, trimmed);
            if (!isSuccessResult(response.result)) {
                setCommentError('Failed to add remark: ' + response.msg);
                return false;
            }
            const entry: HistoryEntry = {
                auditor: UserInfoStore.value,
                action: 'COMMENT',
                timestamp: Date.now(),
                notes: trimmed,
            };
            setReviewData(prev => prev && {
                ...prev,
                history: {
                    ...prev.history,
                    auditTrail: [...prev.history.auditTrail, entry],
                },
            });
            return true;
        } catch (err: any) {
            setCommentError(err.message || 'Failed to add remark. Please try again.');
            return false;
        } finally {
            setAddingComment(false);
        }
    };

    // On a successful decision, tell MCExecute the case is resolved so it closes the task
    // (StopContact) and advances the auditor to the next case. The auditor does NOT wait for the
    // async post-activity execution (offboard/comms) — that continues in EWM in the background.
    // Payload matches the UI LLD's decided contract ({actionStatus: 'SOPComplete'}); MC keys on the
    // event type and this value is a non-sensitive constant, so the '*' targetOrigin is safe.
    const notifyDecisionComplete = () => {
        window.parent.postMessage({type: 'actionStatus', value: {actionStatus: 'SOPComplete'}}, '*');
    };

    const handleDecisionSubmit = async (decision: string, notes: string, reasonCode: string) => {
        if (!reviewId || !reviewData) return;

        // Validate that all required audit check fields have been answered. A field counts as
        // answered if the auditor entered a value OR it arrived pre-populated (field.value) —
        // mirroring the submit fallback below so a pre-filled required field isn't falsely blocked.
        const unanswered = reviewData.auditChecks.filter(
            f => f.required && !(auditCheckResponses[f.label] ?? f.value)
        );
        if (unanswered.length > 0) {
            setSubmitError('Please answer all required audit checks before submitting.');
            return;
        }

        setSubmitting(true);
        setSubmitError(null);
        try {
            // Send every audit check, not just the ones the auditor touched, so the case history
            // shows the full checklist. For a field the auditor did not change, fall back to its
            // pre-populated value (field.value) so system-provided data (BWND rates, block totals,
            // dates, etc.) is preserved in the submitted decision rather than blanked — matching how
            // the field renders on screen. Only genuinely empty fields are submitted as ''.
            const fullAuditCheckResponses = reviewData.auditChecks.reduce<Record<string, string>>(
                (responses, field) => {
                    responses[field.label] = auditCheckResponses[field.label] ?? field.value ?? '';
                    return responses;
                },
                {}
            );

            const request: SubmitDecisionRequest = {
                reviewId,
                reviewStatus: ReviewStatus.COMPLETED,
                // `decision` is a ReviewResult token sourced from the backend `decisions` list; the
                // backend model re-validates it against the ReviewResult enum and remains the source of truth.
                reviewResult: decision as ReviewResult,
                notes: notes || undefined,
                reasonCode: reasonCode || undefined,
                auditCheckResponses: fullAuditCheckResponses
            };

            const response = await LighthouseApi.submitDecision(request);

            if (isSuccessResult(response.result)) {
                if (mode === 'iframe') {
                    notifyDecisionComplete();
                } else if (mode === 'standalone') {
                    await fetchReviewData();
                }
            } else {
                setSubmitError('Failed to submit decision: ' + response.msg);
            }
        } catch (err: any) {
            const errorMsg = err.message || 'Unknown error';
            setSubmitError('Error submitting decision: ' + errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // Build the default "Review" tab content
    const buildReviewTabContent = () => {
        if (!reviewData) return null;
        return (
            <div className="lighthouse-review-content" data-testid="review-tab-content">
                <div className={`lighthouse-grid${isReadOnly ? ' lighthouse-readonly' : ''}`}>
                    <CaseDetailsSection fields={reviewData.caseDetails} />
                    <AuditChecksSection
                        fields={reviewData.auditChecks.map(f => ({
                            ...f,
                            value: auditCheckResponses[f.label] ?? f.value
                        }))}
                        onChange={isReadOnly ? undefined : handleAuditCheckChange}
                    />
                    <DataPointsSection fields={reviewData.dataPoints} />
                    <CaseHistorySection history={reviewData.history} onViewAttachment={handleViewAttachment} />
                </div>
            </div>
        );
    };

    // Build additional tabs from backend config
    const buildAdditionalTabs = (): {label: string; content: React.ReactNode}[] => {
        if (!reviewData?.additionalTabs || reviewData.additionalTabs.length === 0) {
            return [];
        }
        // Warn about unregistered tab component types (aids debugging)
        reviewData.additionalTabs.forEach((tab: AdditionalTab) => {
            if (!TAB_REGISTRY[tab.componentType]) {
                console.warn('[Lighthouse] Unknown tab componentType:', tab.componentType);
            }
        });
        return reviewData.additionalTabs
            .filter((tab: AdditionalTab) => TAB_REGISTRY[tab.componentType])
            .map((tab: AdditionalTab) => {
                const TabComponent = TAB_REGISTRY[tab.componentType];
                return {
                    label: tab.label,
                    content: <TabComponent data={tab.data} />
                };
            });
    };

    // --- RENDER ---

    if (loading) {
        return (
            <div className="lighthouse-loading" data-testid="lighthouse-loading">
                <KatSpinner size="large" />
            </div>
        );
    }

    if (!reviewId) {
        return (
            <div className="lighthouse-waiting" data-testid="lighthouse-waiting">
                <p>No review ID provided.</p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="lighthouse-error" data-testid="lighthouse-error">
                <p className="lighthouse-error-message">{loadError}</p>
            </div>
        );
    }

    if (!reviewData) {
        return (
            <div className="lighthouse-error" data-testid="lighthouse-no-data">
                <p>No review data available.</p>
            </div>
        );
    }

    const tabs = [
        {label: 'Review', content: buildReviewTabContent()},
        ...buildAdditionalTabs()
    ];

    return (
        <div className="lighthouse-page" data-testid="lighthouse-review-page">
            <div className="lighthouse-body">
                <TabContainer tabs={tabs} defaultActiveIndex={0} />
            </div>

            {submitError && (
                <div className="lighthouse-submit-error" data-testid="lighthouse-submit-error">
                    <p>{submitError}</p>
                    <button onClick={() => setSubmitError(null)} data-testid="dismiss-submit-error">Dismiss</button>
                </div>
            )}

            {downloadError && (
                <div className="lighthouse-submit-error" data-testid="lighthouse-download-error">
                    <p>{downloadError}</p>
                    <button onClick={() => setDownloadError(null)} data-testid="dismiss-download-error">Dismiss</button>
                </div>
            )}

            {commentError && (
                <div className="lighthouse-submit-error" data-testid="lighthouse-comment-error">
                    <p>{commentError}</p>
                    <button onClick={() => setCommentError(null)} data-testid="dismiss-comment-error">Dismiss</button>
                </div>
            )}

            {!isReadOnly && (
                <div className="lighthouse-footer" data-testid="lighthouse-footer">
                    <DecisionFooter
                        decisions={reviewData.decisions}
                        reasonCodes={CASE_DECISION_REASON_CODE_CONFIG[reviewData.caseType] ?? DEFAULT_REASON_CODE_OPTION}
                        onSubmit={handleDecisionSubmit}
                        disabled={submitting || addingComment || uploading}
                        onUploadClick={openUploadModal}
                        onAddRemark={handleAddRemark}
                    />
                </div>
            )}

            <UploadFileModal
                visible={uploadModalVisible}
                onClose={closeUploadModal}
                onConfirm={handleUploadConfirm}
                uploading={uploading}
                errorMessage={uploadError}
            />
        </div>
    );
};

export default LighthouseReview;
